import { ChangeEvent, useEffect, useState } from "react";
import axios from "../../../services/axios";
import Constants from "../../../constants/Index";
import { useOnboarding } from "../../../hooks/useOnboarding";
import { ButtonV2, DropdownV2, formatErrorMessageV2, FormCardV2, FormGridV2, OptionV2 } from "../../../components/FormControlsV2";
import { OnboardingFooterV2 } from "../../../components/OnboardingV2/OnboardingFooterV2";
import { Loading } from "../../../components/Loading/Index";
import { persistPayload } from "./adapters/payload";

type AttributeKey = "farmAttribute" | "areaAttribute" | "microAreaAttribute" | "areaHAAttribute";

const attributeLabels: Record<AttributeKey, string> = {
  farmAttribute: "Selecione o atributo da Fazenda",
  areaAttribute: "Selecione o atributo do Retiro",
  microAreaAttribute: "Selecione o atributo do Pasto",
  areaHAAttribute: "Selecione o atributo da unidade de área",
};

const attributeKeys = Object.keys(attributeLabels) as AttributeKey[];

export function ShapeFileV2() {
  const { setPayload, setBlockNextStep } = useOnboarding();
  const [withShapeFile, setWithShapeFile] = useState(false);
  const [shapeFile, setShapeFile] = useState<FormData | null>(null);
  const [optionsAttributes, setOptionsAttributes] = useState<OptionV2[]>([]);
  const [attributes, setAttributes] = useState<Record<AttributeKey, OptionV2 | null>>({
    farmAttribute: null,
    areaAttribute: null,
    microAreaAttribute: null,
    areaHAAttribute: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  function transformPolygonToLatLng(multiPolygon: number[][][]) {
    return multiPolygon.map((areas) => areas.map((area) => ({ lng: area[0], lat: area[1] })));
  }

  async function analyzeShapeFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsLoading(true);
      setError("");
      const formData = new FormData();
      formData.append("file", file);
      setShapeFile(formData);
      const response = await axios.post("shapefile/analyze", formData);
      const analyses = response.data;

      setOptionsAttributes(
        analyses.attributes.map((attribute: string) => ({
          label: attribute,
          value: attribute,
        }))
      );

      setAttributes((current) => {
        const next = { ...current };
        attributeKeys.forEach((field) => {
          const suggestion = analyses.suggestions?.[field];
          if (suggestion) next[field] = { label: suggestion, value: suggestion };
        });
        return next;
      });
    } catch {
      setError(Constants.MESSAGE_INVALID_MAP);
    } finally {
      setIsLoading(false);
    }
  }

  function updateAttribute(key: AttributeKey, option: OptionV2 | null) {
    setAttributes((current) => {
      const next = { ...current, [key]: option };
      attributeKeys.forEach((field) => {
        if (field !== key && option && next[field]?.value === option.value) next[field] = null;
      });
      return next;
    });
  }

  async function submitShapeFile() {
    if (!withShapeFile) return true;
    if (!shapeFile || attributeKeys.some((key) => !attributes[key]?.value)) {
      setError("Informe o shapefile e todos os atributos obrigatórios.");
      return false;
    }

    try {
      setIsLoading(true);
      setError("");
      const params = new URLSearchParams();
      attributeKeys.forEach((key) => {
        const value = attributes[key]?.value;
        if (value) params.append(key, String(value));
      });

      const { data } = await axios.post(`shapefile/polygon/process?${params.toString()}`, shapeFile);
      const payloadResponse = {
        farms: data.farms.map((farm: any, index: number) => ({
          uuid: `farm-${index}`,
          isNew: "true",
          name: farm.name,
          initials: farm.initials,
          document: null,
          state_registration: "",
          address: {
            city: { code: null, name: null },
            state: null,
            street: "",
          },
          manager_user_id: null,
          properties: { season: { dry_initial: 3, rain_initial: 8 } },
          map_coords: transformPolygonToLatLng(farm.geometry.coordinates).flat(),
          filename: null,
        })),
        areas: data.farms
          .map((farm: any, farmId: number) =>
            farm.areas.map((area: any, areaId: number) => ({
              uuid: `${farmId}-area-${areaId}`,
              isNew: "true",
              farm_id: `farm-${farmId}`,
              name: area.name,
              acreage: `${Number(area.area).toFixed(2)}`,
              initials: area.initials,
              indoor_grazing_time: null,
              outdoor_grazing_time: null,
              manager_user_id: null,
              map_coords: {
                uuid: `${farmId}-area-${areaId}`,
                coords: transformPolygonToLatLng(area.geometry.coordinates).flat(),
              },
            }))
          )
          .flat(),
        micro_areas: data.farms
          .map((farm: any, farmId: number) =>
            farm.areas.map((area: any, areaId: number) =>
              area.micro_areas.map((microArea: any, microAreaId: number) => ({
                isNew: "true",
                area_id: `${farmId}-area-${areaId}`,
                uuid: `${farmId}-microArea-${microAreaId}`,
                initials: microArea.name,
                acreage: `${Number(microArea.area).toFixed(2)}`,
                map_coords: {
                  uuid: `${farmId}-microArea-${microAreaId}`,
                  coords: transformPolygonToLatLng(microArea.geometry.coordinates).flat(),
                },
              }))
            )
          )
          .flat()
          .flat(),
      };

      setPayload((current) => {
        const next = { ...current, ...payloadResponse };
        persistPayload(next);
        return next as any;
      });
      return true;
    } catch {
      setError(Constants.MESSAGE_INVALID_MAP);
      return false;
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setBlockNextStep(false);
  }, [setBlockNextStep, withShapeFile]);

  return (
    <>
      <Loading visible={isLoading} />
      <main>
        <div className="onboarding-v2-form-page">
          <FormCardV2 title="Onboarding" description="Escolha entre cadastro manual ou importação por shapefile.">
            <div className="onboarding-v2-choice">
              <ButtonV2 variant={!withShapeFile ? "primary" : "secondary"} onClick={() => setWithShapeFile(false)}>
                Manual
              </ButtonV2>
              <ButtonV2 variant={withShapeFile ? "primary" : "secondary"} onClick={() => setWithShapeFile(true)}>
                ShapeFile
              </ButtonV2>
            </div>

            {withShapeFile && (
              <FormGridV2 columns={1}>
                <label className="onboarding-v2-file">
                  <span>Arquivo shapefile</span>
                  <input type="file" onChange={analyzeShapeFile} />
                </label>
                {attributeKeys.map((key) => (
                  <DropdownV2
                    key={key}
                    name={key}
                    label={attributeLabels[key]}
                    value={attributes[key]}
                    options={optionsAttributes}
                    searchable
                    error={!attributes[key] && error ? "Campo obrigatório" : undefined}
                    onChange={(option) => updateAttribute(key, option)}
                  />
                ))}
              </FormGridV2>
            )}

            {isLoading && <strong className="onboarding-v2-status">Processando shapefile...</strong>}
            {error && <small className="ov2-field__error">{formatErrorMessageV2(error)}</small>}
          </FormCardV2>
        </div>
      </main>
      <OnboardingFooterV2 nextStepEvent={submitShapeFile} />
    </>
  );
}
